/**
 * Mail UI strings [WP-E]. Only WP-E edits this file; it is merged into `zh` by zh.ts, so keys
 * stay typed: `t('mail.list.empty.inbox')`.
 *
 * - `mail`: strings for the mail UI (layout, list, thread view, search, labels, login).
 * - `mailErrors`: messages for API error codes not already in `zh.errors` (merged into
 *   `zh.errors`; add new codes only, never redefine an existing one).
 */
export const mail = {
  shell: {
    logo: 'AZ Mail',
    mainMenu: '主菜单',
    settings: '设置',
    shortcuts: '键盘快捷键',
    offline: '实时更新已断开，正在重新连接…',
    loadingApp: '正在加载 AZ Mail…',
    loadFailed: '无法加载账号信息',
  },
  account: {
    menu: 'AZ Mail 账号：{name}',
    hello: '{name}，您好！',
    manageSettings: '管理设置',
    admin: '管理后台',
    logout: '退出登录',
    loggingOut: '正在退出…',
  },
  search: {
    placeholder: '搜索邮件',
    submit: '搜索',
    clear: '清除搜索',
    options: '显示搜索选项',
    title: '搜索结果',
    empty: '没有符合搜索条件的邮件',
    emptyHint: '请尝试使用其他关键词，或使用搜索选项缩小范围。',
  },
  advanced: {
    title: '高级搜索',
    from: '发件人',
    to: '收件人',
    subject: '主题',
    hasWords: '包含字词',
    doesntHave: '不包含',
    size: '大小',
    sizeLarger: '大于',
    sizeSmaller: '小于',
    sizeValue: '大小数值',
    sizeUnit: '大小单位',
    unitMB: 'MB',
    unitKB: 'KB',
    unitB: '字节',
    dateWithin: '日期范围',
    dateWithinSuffix: '以内',
    date: '日期',
    within: {
      '1d': '1 天',
      '3d': '3 天',
      '1w': '1 周',
      '2w': '2 周',
      '1m': '1 个月',
      '2m': '2 个月',
      '6m': '6 个月',
      '1y': '1 年',
    },
    scope: '搜索范围',
    scopes: {
      all: '所有邮件',
      anywhere: '邮件、垃圾邮件和已删除邮件',
      inbox: '收件箱',
      starred: '已加星标',
      sent: '已发送',
      drafts: '草稿',
      scheduled: '已定时',
      spam: '垃圾邮件',
      trash: '已删除',
      unread: '未读邮件',
      read: '已读邮件',
      label: '标签：{name}',
    },
    hasAttachment: '包含附件',
    reset: '清除',
    search: '搜索',
  },
  nav: {
    compose: '写邮件',
    more: '更多',
    less: '收起',
    labels: '标签',
    createLabel: '创建新标签',
    unreadCount: '{count} 封未读邮件',
    draftCount: '{count} 封草稿',
    labelOptions: '“{name}”的选项',
  },
  list: {
    label: '会话列表',
    select: '选择',
    selectMenu: '选择选项',
    selectAll: '全部',
    selectNone: '无',
    selectRead: '已读',
    selectUnread: '未读',
    selectStarred: '已加星标',
    selectUnstarred: '未加星标',
    refresh: '刷新',
    more: '更多',
    markAllRead: '全部标为已读',
    selected: '已选择 {count} 个会话',
    pageRange: '第 {from}-{to} 行，共 {total} 行',
    pageRangeOpen: '第 {from}-{to} 行',
    newer: '较新',
    older: '较旧',
    loadFailed: '无法加载邮件列表',
    retry: '重试',
    loading: '正在加载邮件…',
    draft: '草稿',
    me: '我',
    toPrefix: '收件人：',
    inbox: '收件箱',
    moreAttachments: '另外 {count} 个附件',
    unreadRow: '未读',
    starred: '已加星标',
    notStarred: '未加星标',
    star: '加星标',
    unstar: '取消星标',
    hasAttachment: '有附件',
    trashNotice: '“已删除”中的邮件会在一段时间后被自动永久删除。',
    spamNotice: '“垃圾邮件”中的邮件会在一段时间后被自动永久删除。',
    empty: {
      inbox: '收件箱中没有邮件',
      inboxHint: '新邮件到达后会显示在这里。',
      starred: '没有已加星标的邮件',
      starredHint: '为邮件加星标后，可以在这里快速找到它们。',
      scheduled: '没有已定时的邮件',
      scheduledHint: '定时发送的邮件会在发送前显示在这里。',
      sent: '没有已发送的邮件',
      sentHint: '发送一封邮件吧！',
      drafts: '没有已保存的草稿',
      draftsHint: '保存草稿后，可以随时回来继续撰写。',
      all: '没有邮件',
      allHint: '',
      spam: '太棒了，没有垃圾邮件！',
      spamHint: '',
      trash: '“已删除”中没有邮件',
      trashHint: '',
      label: '此标签下没有邮件',
      labelHint: '为会话添加此标签后，会显示在这里。',
    },
  },
  actions: {
    archive: '归档',
    moveToInbox: '移至收件箱',
    reportSpam: '举报垃圾邮件',
    notSpam: '不是垃圾邮件',
    delete: '删除',
    deleteForever: '永久删除',
    restore: '恢复',
    markRead: '标记为已读',
    markUnread: '标记为未读',
    star: '加星标',
    unstar: '取消星标',
    moveTo: '移至',
    labels: '标签',
    more: '更多',
    undo: '撤销',
    deleteForeverTitle: '永久删除 {count} 个会话？',
    deleteForeverMessage: '此操作无法撤消，会话中的所有邮件都将被永久删除。',
  },
  toasts: {
    archived: '已归档 {count} 个会话',
    trashed: '已将 {count} 个会话移至“已删除”',
    spammed: '已将 {count} 个会话标记为垃圾邮件',
    notSpam: '已将 {count} 个会话移至收件箱',
    restored: '已恢复 {count} 个会话',
    movedToInbox: '已将 {count} 个会话移至收件箱',
    deletedForever: '已永久删除 {count} 个会话',
    markedRead: '已将 {count} 个会话标记为已读',
    markedUnread: '已将 {count} 个会话标记为未读',
    labelAdded: '已为 {count} 个会话添加标签“{name}”',
    labelRemoved: '已从 {count} 个会话移除标签“{name}”',
    movedToLabel: '已将 {count} 个会话移至“{name}”',
    undone: '已撤消操作',
    failed: '操作失败：{message}',
  },
  thread: {
    back: '返回{folder}',
    backToSearch: '返回搜索结果',
    backToLabel: '返回“{name}”',
    loading: '正在加载会话…',
    loadFailed: '无法加载此会话',
    notFound: '找不到此会话，它可能已被删除。',
    backToList: '返回列表',
    position: '第 {index} 封，共 {total} 封',
    newer: '较新',
    older: '较旧',
    expandAll: '全部展开',
    collapseAll: '全部收起',
    olderMessages: '显示 {count} 封较早的邮件',
    trashedHidden: '此会话中有 {count} 封已删除的邮件。',
    trashedShow: '查看邮件',
    trashedHide: '隐藏邮件',
    spamHidden: '此会话中有 {count} 封垃圾邮件。',
    nonTrashedHidden: '此会话中还有 {count} 封未删除的邮件。',
    removeLabel: '移除标签“{name}”',
    removeInbox: '从收件箱中移除（归档）',
    draft: '草稿',
    openDraft: '继续编辑草稿',
    toMe: '我',
    toMeBcc: '密送给我',
    sendTo: '发送至 {names}',
    showDetails: '显示详细信息',
    sentBy: '由 {name} 发送',
    replyTo: '回复地址',
    details: {
      from: '发件人：',
      replyTo: '回复地址：',
      to: '收件人：',
      cc: '抄送：',
      bcc: '密送：',
      date: '日期：',
      subject: '主题：',
      deliveredTo: '送达地址：',
      security: '安全：',
      sentBy: '实际发件人：',
    },
    auth: {
      pass: '通过',
      fail: '未通过',
      none: '无',
      unknown: '未知',
      line: 'SPF {spf} · DKIM {dkim} · DMARC {dmarc}',
    },
    warnings: {
      dmarc_fail: '此邮件未通过发件人身份验证（DMARC），可能并非来自其声称的发件人。请勿点击其中的链接或回复个人信息。',
      spoofed_internal: '此邮件声称来自本团队的地址，但未通过身份验证，很可能是伪造的。请谨慎对待。',
    },
    spamBanner: '此邮件位于“垃圾邮件”中。',
    spamBannerAction: '报告为非垃圾邮件',
    menu: {
      label: '更多选项',
      reply: '回复',
      replyAll: '回复全部',
      forward: '转发',
      showOriginal: '显示原始邮件',
      deleteMessage: '删除此邮件',
      deleteMessageUnavailable: '只能删除整个会话',
      markUnreadFromHere: '从此处标记为未读',
    },
    reply: '回复',
    replyAll: '回复全部',
    forward: '转发',
    starMessage: '为此邮件加星标',
    unstarMessage: '取消此邮件的星标',
    showQuoted: '显示引用的文字',
    hideQuoted: '隐藏引用的文字',
    plainText: '纯文本邮件',
    emptyBody: '（此邮件没有正文）',
    frameTitle: '邮件内容：{subject}',
    markedUnreadFromHere: '已从此处标记为未读',
  },
  images: {
    hidden: '已隐藏外部图片',
    show: '显示图片',
    alwaysFrom: '始终显示来自 {sender} 的图片',
    trusted: '以后将始终显示来自 {sender} 的图片',
    spamHint: '为保护您的隐私，垃圾邮件和可疑邮件中的图片不会自动显示。',
  },
  attachments: {
    title: '{count} 个附件',
    download: '下载 {name}',
    preview: '预览 {name}',
    open: '在新标签页中打开',
    previewTitle: '附件预览',
    noPreview: '此文件无法预览',
  },
  delivery: {
    title: '投递状态',
    timeline: '投递记录',
    loading: '正在加载投递记录…',
    loadFailed: '无法加载投递记录',
    empty: '暂无投递记录',
    retry: '重新发送',
    retrying: '正在重新发送…',
    retried: '已重新加入发送队列',
    cancelSchedule: '取消定时发送',
    canceled: '已取消定时发送，邮件已转为草稿',
    reschedule: '更改发送时间',
    rescheduleConfirm: '更改时间',
    rescheduling: '正在更改发送时间…',
    rescheduled: '已将发送时间改为 {time}',
    rescheduleTooLate: '邮件已发出，无法更改发送时间',
    events: {
      email_sent: '已发送至邮件服务',
      email_scheduled: '已定时',
      email_delivered: '已送达',
      email_delivery_delayed: '投递延迟',
      email_bounced: '退信',
      email_complained: '被收件人标记为垃圾邮件',
      email_failed: '发送失败',
      email_suppressed: '已被抑制',
      email_opened: '已打开',
      email_clicked: '链接被点击',
      local_queued: '已加入发送队列',
      local_accepted: '已提交',
      local_failed: '发送失败',
      local_canceled: '已取消',
      local_rescheduled: '已更改定时',
      other: '{type}',
    },
  },
  labels: {
    createTitle: '新建标签',
    editTitle: '修改标签',
    name: '标签名称',
    namePlaceholder: '请输入新标签名称',
    nameRequired: '请输入标签名称',
    nameTooLong: '标签名称不能超过 {max} 个字符',
    color: '标签颜色',
    colorOption: '颜色 {color}',
    create: '创建',
    save: '保存',
    created: '已创建标签“{name}”',
    updated: '已更新标签“{name}”',
    deleted: '已删除标签“{name}”',
    deleteTitle: '删除标签“{name}”？',
    deleteMessage: '带有此标签的会话不会被删除，只会移除此标签。',
    menuEdit: '修改',
    menuColor: '标签颜色',
    menuDelete: '删除标签',
    apply: '标签',
    filter: '搜索标签',
    none: '没有标签',
    noMatch: '没有匹配的标签',
    createNamed: '新建“{name}”',
    createNew: '新建标签',
  },
  move: {
    title: '移至',
    filter: '搜索文件夹或标签',
    inbox: '收件箱',
    spam: '垃圾邮件',
    trash: '已删除',
  },
  shortcuts: {
    title: '键盘快捷键',
    groups: {
      navigation: '导航',
      actions: '操作',
      thread: '会话',
    },
    seqThen: '然后',
    or: '或',
    hint: '在输入框或编辑器中输入时，快捷键不会生效。',
    items: {
      compose: '写邮件',
      search: '搜索邮件',
      next: '较旧的会话 / 下一行',
      prev: '较新的会话 / 上一行',
      open: '打开会话',
      back: '返回会话列表',
      goInbox: '转到收件箱',
      goStarred: '转到已加星标',
      goSent: '转到已发送',
      goDrafts: '转到草稿',
      goAll: '转到所有邮件',
      archive: '归档',
      delete: '删除',
      spam: '举报垃圾邮件',
      star: '加星标 / 取消星标',
      select: '选择会话',
      markRead: '标记为已读',
      markUnread: '标记为未读',
      reply: '回复',
      replyAll: '回复全部',
      forward: '转发',
      help: '显示键盘快捷键',
    },
  },
  login: {
    title: '登录',
    subtitle: '使用您的 AZ Mail 账号',
    email: '邮箱地址',
    password: '密码',
    showPassword: '显示密码',
    submit: '登录',
    emailRequired: '请输入邮箱地址',
    passwordRequired: '请输入密码',
    retryIn: '尝试次数过多，请在 {time}后重试',
    seconds: '{n} 秒',
    minutes: '{n} 分钟',
    footer: '团队内部邮件系统 · 如需账号请联系管理员',
  },
  notFound: {
    title: '找不到该页面',
    message: '您访问的地址不存在，或您没有权限查看。',
    back: '返回收件箱',
  },
  documentTitle: {
    folder: '{name} - {email} - AZ Mail',
    folderUnread: '{name} ({count}) - {email} - AZ Mail',
  },
} as const;

export const mailErrors = {
  account_disabled: '此账号已被停用，请联系管理员',
  label_exists: '已存在同名标签',
  /** 409 POST /api/labels; `{limit}` = details.limit (500). */
  too_many_labels: '标签数量已达上限（{limit} 个），请先删除不用的标签',
  invalid_state: '当前状态下无法执行此操作',
  invalid_signature: '链接已失效，请刷新页面后重试',
} as const;
